/**
 * createSceneCinematicHost — adapts a Phaser scene (ideally an IWorldScene) to the CinematicHost
 * interface consumed by CinematicPlayer. Every capability degrades gracefully when the scene lacks it.
 */
import Phaser from 'phaser';
import type { BattleOutcome, IActor, IDialogueRunner, IEffectsDirector, IWorldScene } from '@/core/types/contracts';
import type { WeatherState } from '@/core/types';
import { ctxOf, type GameContext } from '@/core/context';
import { SceneKeys } from '@/core/scenes/keys';
import { isStoryFlag } from '@/story/flags';
import { audio } from '@/audio';
import { createEffects } from '@/effects';
import { runBattle } from '@/combat';
import { CinematicPlayer, type CinematicHost } from './CinematicPlayer';
import { getTimeline } from './registry';
import { MEMORY_STAGES, runMemoryStage } from './MemoryStage';

/** Story-state access used by the host; defaults to the shared GameContext story. */
export interface CinematicStateAccess {
  hasMark(mark: string): boolean;
  setMark(mark: string): void;
  setFlag(flag: string): void;
  /** "Reached" test for dialogue conditions; defaults to the context story when absent. */
  hasFlag?(flag: string): boolean;
}

export interface SceneHostDeps {
  dialogue: IDialogueRunner & { abort?(): void; fastForward?(id: string): void };
  ctx?: GameContext;
  /** Actor lookup; defaults to `scene.actors` when the scene is an IWorldScene. */
  actors?: Map<string, IActor>;
  state?: CinematicStateAccess;
  effects?: IEffectsDirector | null;
  letter?: (paragraph: string, opts: { instant: boolean }) => Promise<void>;
  memory?: (stage: string, opts: { fastForward: boolean }) => Promise<'completed' | 'skipped'>;
  battle?: (config: string) => Promise<BattleOutcome>;
}

type SceneLike = Phaser.Scene & Partial<Pick<IWorldScene, 'actors' | 'player' | 'map' | 'setWeather' | 'cameraPanTo' | 'cameraFollowPlayer' | 'cameraShake' | 'setInputLocked'>>;

/** Resolves when `run` calls done, or when the scene shuts down (never hangs a dead scene). */
function guarded(scene: Phaser.Scene, run: (done: () => void) => void): Promise<void> {
  return new Promise<void>((resolve) => {
    let finished = false;
    const done = () => { if (finished) return; finished = true; scene.events.off(Phaser.Scenes.Events.SHUTDOWN, done); resolve(); };
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, done);
    try { run(done); } catch (e) { console.warn('[cinematic-host] step failed', e); done(); }
  });
}

function hexToRgb(color: string | undefined): [number, number, number] {
  if (!color) return [0, 0, 0];
  const c = Phaser.Display.Color.HexStringToColor(color);
  return [c.red, c.green, c.blue];
}

export function stateFromContext(ctx: GameContext): CinematicStateAccess {
  return {
    hasMark: (m) => ctx.story.hasMark(m),
    setMark: (m) => void ctx.story.mark(m),
    setFlag: (f) => { if (isStoryFlag(f)) ctx.story.advance(f); else console.warn(`[cinematic] unknown flag "${f}"`); },
    hasFlag: (f) => isStoryFlag(f) && ctx.story.has(f),
  };
}

/**
 * State-only replay of a memory stage's timeline (used when a skipped cinematic reaches
 * `launchMemory`): applies setFlag/setMark/silent dialogue effects without launching the scene.
 */
export function replayMemoryState(timelineId: string, state: CinematicStateAccess, dialogue: SceneHostDeps['dialogue']): Promise<'completed' | 'skipped'> {
  if (!getTimeline(timelineId)) { console.warn(`[cinematic-host] memory timeline "${timelineId}" not registered — nothing to replay`); return Promise.resolve('skipped'); }
  const headless: CinematicHost = {
    say: () => Promise.resolve(),
    sayFastForward: (id) => dialogue.fastForward?.(id),
    setFlag: (f) => state.setFlag(f),
    setMark: (m) => state.setMark(m),
    hasMark: (m) => state.hasMark(m),
    wait: () => Promise.resolve(),
    launchMemory: () => Promise.resolve('skipped'),
  };
  return new CinematicPlayer(headless, { skipOnCancelEvent: false }).play(timelineId, { fastForward: true });
}

export function createSceneCinematicHost(scene: Phaser.Scene, deps: SceneHostDeps): CinematicHost {
  const s = scene as SceneLike;
  const ctx = deps.ctx ?? ctxOf(scene);
  const state = deps.state ?? stateFromContext(ctx);
  const actors = deps.actors ?? (s.actors instanceof Map ? s.actors : new Map<string, IActor>());
  let effects: IEffectsDirector | null | undefined = deps.effects;
  /** Saved outer skip handlers, one per registered player (outer → nested → nested …), popped LIFO. */
  const skipStack: ((() => boolean) | null)[] = [];
  const cam = () => scene.cameras.main;
  const uiScene = () => scene.scene.get(SceneKeys.UI) as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;

  return {
    say: (id) => deps.dialogue.run(id),
    sayFastForward: (id) => deps.dialogue.fastForward?.(id),
    onSkip: () => deps.dialogue.abort?.(),
    setFlag: (f) => state.setFlag(f),
    setMark: (m) => state.setMark(m),
    hasMark: (m) => state.hasMark(m),
    wait: (ms) => guarded(scene, (done) => { if (ms <= 0) done(); else scene.time.delayedCall(ms, done); }),
    actor: (id) => actors.get(id) ?? (id === 'player' ? s.player : undefined),
    camera: {
      panTo: (x, y, ms) => {
        if (s.cameraPanTo) return s.cameraPanTo(x, y, ms);
        return guarded(scene, (done) => {
          if (ms <= 0) { cam().centerOn(Math.round(x), Math.round(y)); done(); return; }
          cam().pan(Math.round(x), Math.round(y), ms, 'Sine.easeInOut', true, (_c: Phaser.Cameras.Scene2D.Camera, progress: number) => { if (progress >= 1) done(); });
        });
      },
      follow: () => { if (s.cameraFollowPlayer) s.cameraFollowPlayer(); else if (s.player) cam().startFollow(s.player.sprite, true); },
      shake: (ms, intensity) => { if (s.cameraShake) s.cameraShake(ms, intensity); else cam().shake(ms, intensity, true); },
      zoom: (z, ms) => guarded(scene, (done) => {
        if (ms <= 0) { cam().setZoom(z); done(); return; }
        cam().zoomTo(z, ms, 'Sine.easeInOut', true, (_c: Phaser.Cameras.Scene2D.Camera, progress: number) => { if (progress >= 1) done(); });
      }),
    },
    weather: (st: WeatherState, ms) => { if (s.setWeather) s.setWeather(st, ms); else ctx.setWeather(st); },
    fx: (effect, params) => {
      if (effects === undefined) effects = createEffects(s as IWorldScene); // integrator seam fix: memory stages (no map) still get one-shot effects
      if (!effects) { console.warn(`[cinematic-host] no effects director for fx "${effect}"`); return Promise.resolve(); }
      return effects.play(effect, params);
    },
    sfx: (key) => audio().sfx(key),
    music: (key, fadeMs) => audio().playMusic(key, fadeMs),
    fade: (out, ms, color) => {
      const ui = uiScene();
      if (ui?.fade && ui.scene.isActive()) return ui.fade(out, ms);
      const [r, g, b] = hexToRgb(color);
      return guarded(scene, (done) => {
        if (ms <= 0) { if (out) cam().fadeOut(1, r, g, b); else cam().fadeIn(1, r, g, b); done(); return; }
        const cb = (_c: Phaser.Cameras.Scene2D.Camera, progress: number) => { if (progress >= 1) done(); };
        if (out) cam().fadeOut(ms, r, g, b, cb); else cam().fadeIn(ms, r, g, b, cb);
      });
    },
    letter: (paragraph, opts) => {
      if (deps.letter) return deps.letter(paragraph, opts);
      console.warn('[cinematic-host] no letter presenter — paragraph skipped');
      return Promise.resolve();
    },
    launchMemory: (stage, opts) => {
      if (deps.memory) return deps.memory(stage, opts);
      const def = MEMORY_STAGES[stage];
      const timelineId = def?.timeline ?? stage;
      // Skipped cinematic: replay the stage's state without flashing its scene.
      if (opts.fastForward && getTimeline(timelineId)) return replayMemoryState(timelineId, state, deps.dialogue);
      return runMemoryStage(scene, stage, timelineId, { fastForward: opts.fastForward, state });
    },
    battle: (config) => (deps.battle ? deps.battle(config) : runBattle(scene, config)),
    setInputLocked: (locked) => s.setInputLocked?.(locked),
    registerSkip: (fn) => {
      if (fn) { skipStack.push(ctx.skipHandler); ctx.skipHandler = fn; return; }
      if (skipStack.length === 0) { ctx.skipHandler = null; return; }
      ctx.skipHandler = skipStack.pop() ?? null;
    },
  };
}
